import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  ExtendedRemoteFile,
  FileListNode,
} from '@/components/LexicalEditor/nodes/FileListNode';
import AttachmentFile from '@/components/AttachmentFile';
import { useGetFileLink, useUploadFile } from '@/hooks/apiHooks';
import { $getNodeByKey } from 'lexical';
import CommentFiles from '@/components/Comment/CommentList/CommentItem/CommentFiles';
import ApprovalAttachmentList from '@/components/Comment/CommentList/CommentItem/ApprovalAttachment';

type FileUploaderRef = {};
type FileListComponentProps = { node: FileListNode };

const getFileSize = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;

  const fileSize = Number(value);
  return Number.isFinite(fileSize) ? fileSize : undefined;
};

const FileListComponent = forwardRef<FileUploaderRef, FileListComponentProps>(
  ({ node }, ref) => {
    const [editor] = useLexicalComposerContext();
    const [localFiles, setLocalFiles] = useState<ExtendedRemoteFile[]>(
      node.__files,
    );

    const hasUploadedRef = React.useRef(false);

    const updateEditorStatus = (updatedFiles: ExtendedRemoteFile[]) => {
      setLocalFiles(updatedFiles);

      editor.update(() => {
        const nodeKey = node.getKey();
        const targetNode = $getNodeByKey(nodeKey) as FileListNode | null;
        if (!targetNode) return;

        if (updatedFiles.length === 0) {
          targetNode.remove();
        } else {
          const newNode = new FileListNode(updatedFiles, targetNode.__variant);
          targetNode.replace(newNode);
        }
      });
    };

    useEffect(() => {
      if (hasUploadedRef.current) return;
      const files = node.__files;
      const uploadAll = async (files: ExtendedRemoteFile[]) => {
        const results = await Promise.all(
          files.map(async (f) => {
            if (f.status === 'loading' && f.file) {
              try {
                const { data: uploadData } = await useUploadFile(f.file);
                const { data: downloadData } = await useGetFileLink(
                  uploadData.uuid,
                );
                return {
                  ...f,
                  link: downloadData.url,
                  uuid: uploadData.uuid,
                  fileUuid: uploadData.uuid,
                  fileSize:
                    uploadData.fileSizeInBytes ??
                    getFileSize(uploadData.fileSize) ??
                    f.file?.size,
                  createdAt: uploadData.createdAt,
                  status: 'success' as const,
                };
              } catch {
                return {
                  ...f,
                  status: 'error' as const,
                };
              }
            }
            return f;
          }),
        );

        updateEditorStatus(results);
      };

      const filesToUpload = files.filter(
        (f) => f.status === 'loading' && f.file,
      );

      if (filesToUpload.length > 0) {
        hasUploadedRef.current = true;
        uploadAll(filesToUpload);
      }
    }, [node, editor]);

    const handleRemoveItem = (itemToRemove: ExtendedRemoteFile) => {
      updateEditorStatus(
        localFiles.filter(
          (f) =>
            !(f.name === itemToRemove.name && f.link === itemToRemove.link),
        ),
      );
    };

    useImperativeHandle(ref, () => ({}));

    if (localFiles.length === 0) return null;

    if (node.__variant === 'approval') {
      return (
        <ApprovalAttachmentList
          items={localFiles.map((item) => ({
            name: item.name,
            link: item.link,
            status: item.status,
            fileUuid: item.fileUuid ?? item.uuid,
            fileSize: item.fileSize,
            createdAt: item.createdAt,
          }))}
          onRemove={(item) =>
            handleRemoveItem(
              localFiles.find(
                (f) => f.name === item.name && f.link === item.link,
              ) ?? (item as ExtendedRemoteFile),
            )
          }
        />
      );
    }

    return (
      <CommentFiles>
        {localFiles.map((item) => (
          <AttachmentFile
            key={item.link || item.name}
            file={item}
            isDeletable={true}
            isPreviewable={false}
            status={item.status}
            onSuccess={() => handleRemoveItem(item)}
          />
        ))}
      </CommentFiles>
    );
  },
);

FileListComponent.displayName = 'FileListComponent';

export default FileListComponent;
